import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { DocumentoOficial, Empregador, TipoDocumentoOficial } from '../../api.service';
import {
  itensNavegacaoModuloGerencial,
  moduloGerencialPorUrl,
  ModuloGerencial
} from '../../core/rotas/navegacao-menu-gerencial';
import { LayoutModuloGerencialComponent } from '../../layout/layout-modulo-gerencial.component';
import { ServicoEmpregadorAdministrativo } from '../empregador/servico-empregador-administrativo';
import { ServicoSessaoGerencial } from '../menu/servico-sessao-gerencial';
import { ServicoDocumentoOficial } from './servico-documento-oficial';
import { ajustarPeriodoListagem, filtroListagemDocumentosPadrao } from './regras-listagem-documentos';

@Component({
  selector: 'app-tela-gerenciador-oficios',
  standalone: true,
  imports: [CommonModule, FormsModule, LayoutModuloGerencialComponent],
  templateUrl: './tela-gerenciador-oficios.component.html',
  styleUrls: ['../../shared/modal-pesquisa-lista.component.css', './tela-gerenciador-oficios.component.css']
})
export class TelaGerenciadorOficiosComponent implements OnInit, OnDestroy {
  agora = new Date();
  aba: ModuloGerencial = 'oficios';
  mensagem = '';
  empregadores: Empregador[] = [];
  empregadorSelecionado = '';
  cadastros = this.formulariosVazios();
  dataInicial = filtroListagemDocumentosPadrao().dataInicial;
  dataFinal = filtroListagemDocumentosPadrao().dataFinal;
  tipoFiltro: TipoDocumentoOficial | '' = '';
  documentos: DocumentoOficial[] = [];
  pagina = 1;
  limite = filtroListagemDocumentosPadrao().limite;
  totalElementos = 0;
  totalPaginas = 0;
  edicaoId = '';
  modalReservaAberta = false;
  reservaTipo?: TipoDocumentoOficial;
  reservaRotulo = '';
  reservaAno = new Date().getFullYear();
  reservaQuantidade: number | null = 1;
  private rascunhoEdicao?: DocumentoOficial;
  private atualizacaoRelogio?: number;
  private assinaturaRoteador?: Subscription;

  constructor(
    readonly sessao: ServicoSessaoGerencial,
    private readonly servicoEmpregador: ServicoEmpregadorAdministrativo,
    private readonly servicoDocumento: ServicoDocumentoOficial,
    private readonly roteador: Router
  ) {}

  get itensMenu() {
    return itensNavegacaoModuloGerencial(this.aba === 'oficios-lista' ? 'oficios-lista' : 'oficios');
  }

  ngOnInit() {
    const padrao = filtroListagemDocumentosPadrao();
    this.dataInicial = padrao.dataInicial;
    this.dataFinal = padrao.dataFinal;
    this.pagina = padrao.pagina;
    this.limite = padrao.limite;
    this.sincronizarAba(this.roteador.url);
    this.atualizacaoRelogio = window.setInterval(() => this.agora = new Date(), 1000);
    this.assinaturaRoteador = this.roteador.events
      .pipe(filter((evento): evento is NavigationEnd => evento instanceof NavigationEnd))
      .subscribe(evento => this.sincronizarAba(evento.urlAfterRedirects));
    this.servicoEmpregador.listar().subscribe({
      next: empregadores => {
        this.empregadores = empregadores;
        this.empregadorSelecionado = this.empregadorSelecionado
          || this.sessao.empregadorSelecionado
          || this.sessao.usuario?.empregadorId
          || this.sessao.usuario?.empregador?.id
          || empregadores[0]?.id
          || '';
        this.sessao.empregadores = empregadores;
        this.sessao.empregadorSelecionado = this.empregadorSelecionado;
        this.carregarCadastro();
        this.filtrarLista();
      },
      error: () => this.mensagem = 'Nao foi possivel carregar empregadores.'
    });
  }

  ngOnDestroy() {
    if (this.atualizacaoRelogio) {
      window.clearInterval(this.atualizacaoRelogio);
    }
    this.assinaturaRoteador?.unsubscribe();
  }

  tipos() {
    return this.servicoDocumento.tipos();
  }

  rotuloTipo(tipo: TipoDocumentoOficial) {
    return this.servicoDocumento.rotuloTipo(tipo);
  }

  formatarData(valor?: string) {
    return this.servicoDocumento.formatarData(valor);
  }

  resumoLista() {
    if (!this.totalElementos) {
      return '';
    }
    return this.totalPaginas > 1
      ? `${this.totalElementos} documentos · pagina ${this.pagina} de ${this.totalPaginas}`
      : `${this.totalElementos} documento(s) neste periodo`;
  }

  aoTrocarEmpregador() {
    this.sessao.empregadorSelecionado = this.empregadorSelecionado;
    this.carregarCadastro();
    this.filtrarLista();
  }

  salvarCadastro(cadastro: { tipo: TipoDocumentoOficial; numero: number | null; ano: number }) {
    if (!this.empregadorSelecionado) {
      this.mensagem = 'Selecione o empregador.';
      return;
    }
    if (cadastro.numero == null || cadastro.numero < 1 || !cadastro.ano) {
      this.mensagem = 'Informe numero e ano.';
      return;
    }
    this.servicoDocumento.cadastrar(this.empregadorSelecionado, cadastro.tipo, cadastro.numero, cadastro.ano).subscribe({
      next: documento => {
        this.mensagem = `${this.rotuloTipo(documento.tipo)} ${documento.numero}/${documento.ano} salvo.`;
        this.carregarCadastro();
      },
      error: erro => this.mensagem = erro?.error?.detail || 'Nao foi possivel salvar o documento.'
    });
  }

  carregarCadastro() {
    if (!this.empregadorSelecionado) {
      this.cadastros = this.formulariosVazios();
      return;
    }
    this.servicoDocumento.listarCadastros(this.empregadorSelecionado).subscribe({
      next: documentos => {
        this.cadastros = this.formulariosVazios().map(formulario => {
          const atual = documentos.find(item => item.tipo === formulario.tipo);
          return atual
            ? {
                ...formulario,
                id: atual.id,
                numero: atual.numero,
                ano: atual.ano,
                dataSolicitacao: atual.dataSolicitacao
              }
            : formulario;
        });
      },
      error: () => this.mensagem = 'Nao foi possivel carregar os numeros cadastrados.'
    });
  }

  alterarDataInicial(valor: string) {
    const periodo = ajustarPeriodoListagem(valor, this.dataFinal);
    this.dataInicial = periodo.dataInicial;
    this.dataFinal = periodo.dataFinal;
  }

  alterarDataFinal(valor: string) {
    const periodo = ajustarPeriodoListagem(this.dataInicial, valor);
    this.dataInicial = periodo.dataInicial;
    this.dataFinal = periodo.dataFinal;
  }

  filtrarLista() {
    this.pagina = 1;
    this.carregarLista();
  }

  irParaPagina(pagina: number) {
    if (pagina < 1 || (this.totalPaginas > 0 && pagina > this.totalPaginas) || pagina === this.pagina) {
      return;
    }
    this.pagina = pagina;
    this.carregarLista();
  }

  carregarLista() {
    if (this.aba !== 'oficios-lista' || !this.empregadorSelecionado) {
      return;
    }
    const periodo = ajustarPeriodoListagem(this.dataInicial, this.dataFinal);
    this.dataInicial = periodo.dataInicial;
    this.dataFinal = periodo.dataFinal;
    this.servicoDocumento.listar(
      this.empregadorSelecionado,
      this.dataInicial,
      this.dataFinal,
      this.tipoFiltro || undefined,
      this.pagina,
      this.limite
    ).subscribe({
      next: pagina => {
        this.documentos = pagina.conteudo;
        this.pagina = pagina.pagina;
        this.limite = pagina.tamanho;
        this.totalElementos = pagina.totalElementos;
        this.totalPaginas = pagina.totalPaginas;
        this.edicaoId = '';
        this.rascunhoEdicao = undefined;
      },
      error: () => this.mensagem = 'Nao foi possivel carregar a listagem.'
    });
  }

  emEdicao(item: DocumentoOficial) {
    return this.edicaoId === item.id;
  }

  editar(item: DocumentoOficial) {
    this.edicaoId = item.id;
    this.rascunhoEdicao = { ...item };
  }

  limparEdicao() {
    if (this.rascunhoEdicao) {
      const indice = this.documentos.findIndex(item => item.id === this.rascunhoEdicao?.id);
      if (indice >= 0) {
        this.documentos[indice] = { ...this.rascunhoEdicao };
      }
    }
    this.edicaoId = '';
    this.rascunhoEdicao = undefined;
  }

  abrirReserva(cadastro: { tipo: TipoDocumentoOficial; rotulo: string; ano: number }) {
    if (!this.empregadorSelecionado) {
      this.mensagem = 'Selecione o empregador.';
      return;
    }
    this.reservaTipo = cadastro.tipo;
    this.reservaRotulo = cadastro.rotulo;
    this.reservaAno = cadastro.ano || new Date().getFullYear();
    this.reservaQuantidade = 1;
    this.modalReservaAberta = true;
  }

  fecharReserva() {
    this.modalReservaAberta = false;
  }

  confirmarReserva() {
    if (!this.empregadorSelecionado || !this.reservaTipo) {
      this.mensagem = 'Selecione o empregador.';
      return;
    }
    if (this.reservaQuantidade == null || this.reservaQuantidade < 1) {
      this.mensagem = 'Informe a quantidade que deseja reservar.';
      return;
    }
    this.servicoDocumento.reservar(
      this.empregadorSelecionado,
      this.reservaTipo,
      this.reservaAno,
      this.reservaQuantidade
    ).subscribe({
      next: documentos => {
        const primeiro = documentos[0];
        const ultimo = documentos[documentos.length - 1];
        const nome = primeiro?.solicitante || this.sessao.usuario?.nome || '';
        this.mensagem = documentos.length === 1
          ? `${this.rotuloTipo(primeiro.tipo)} ${primeiro.numero}/${primeiro.ano} reservado para ${nome}.`
          : `${documentos.length} numeros reservados (${primeiro.numero} a ${ultimo.numero}/${primeiro.ano}) para ${nome}.`;
        this.fecharReserva();
        this.carregarCadastro();
      },
      error: erro => this.mensagem = erro?.error?.detail || 'Nao foi possivel reservar os numeros.'
    });
  }

  salvarLista(item: DocumentoOficial) {
    this.servicoDocumento.editar(item).subscribe({
      next: atualizado => {
        this.mensagem = 'Documento atualizado.';
        Object.assign(item, atualizado);
        this.edicaoId = '';
        this.rascunhoEdicao = undefined;
      },
      error: erro => this.mensagem = erro?.error?.detail || 'Nao foi possivel atualizar o documento.'
    });
  }

  excluirSolicitante(item: DocumentoOficial) {
    this.servicoDocumento.removerSolicitante(item.id).subscribe({
      next: atualizado => {
        this.mensagem = 'Solicitante e departamento removidos. O numero foi mantido.';
        Object.assign(item, atualizado);
      },
      error: erro => this.mensagem = erro?.error?.detail || 'Nao foi possivel remover o solicitante.'
    });
  }

  private sincronizarAba(url: string) {
    const modulo = moduloGerencialPorUrl(url);
    this.aba = modulo === 'oficios-lista' ? 'oficios-lista' : 'oficios';
    this.carregarCadastro();
    this.carregarLista();
  }

  private formulariosVazios() {
    return this.tipos().map(tipo => ({
      id: undefined as string | undefined,
      tipo: tipo.id,
      rotulo: tipo.rotulo,
      numero: null as number | null,
      ano: new Date().getFullYear(),
      dataSolicitacao: undefined as string | undefined
    }));
  }
}
